import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Stethoscope, Building2, MessageCircle, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CareTeam() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [careTeam, setCareTeam] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCareTeam();
  }, []);

  const fetchCareTeam = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/care-team/my-team`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCareTeam(data);
      }
    } catch (error) {
      console.error('Failed to fetch care team:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardContent className="py-8">
          <div className="h-20 bg-gray-100 rounded"></div>
        </CardContent>
      </Card>
    );
  }

  if (!careTeam?.has_care_team) {
    return null; // Don't show if no care team
  }

  const { care_team, partner_name } = careTeam;

  return (
    <Card data-testid="care-team-card" className="border-0 shadow-md overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-400 text-white pb-4">
        <CardTitle className="text-lg flex items-center gap-2">
          <Stethoscope className="w-5 h-5" />
          {t('care_team.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-3">
        {/* Dietitian */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
            {care_team.picture ? (
              <img 
                src={care_team.picture} 
                alt={care_team.name}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <Stethoscope className="w-6 h-6 text-orange-600" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 truncate">{care_team.name}</p>
            <p className="text-sm text-gray-500">{t('care_team.your_dietitian')}</p>
          </div>
          <Button 
            variant="ghost" 
            size="icon"
            data-testid="message-dietitian-btn"
            onClick={() => navigate(`/messages/${care_team.dietitian_id}`)}
            className="flex-shrink-0"
          >
            <MessageCircle className="w-5 h-5 text-orange-500" />
          </Button>
        </div>

        {/* Organization */}
        {partner_name && (
          <div className="flex items-center gap-3 pt-2 border-t">
            <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-600 truncate">{partner_name}</p>
              <p className="text-xs text-gray-400">{t('care_team.partner_organization')}</p>
            </div>
          </div>
        )}

        {/* View details link */}
        <Button 
          variant="ghost" 
          className="w-full justify-between text-orange-600 hover:text-orange-700 hover:bg-orange-50 mt-2"
          onClick={() => navigate('/care-team')}
          data-testid="view-care-team-btn"
        >
          {t('care_team.view_care_team_details')}
          <ChevronRight className="w-4 h-4" />
        </Button>
      </CardContent>
    </Card>
  );
}
